// The dig's per-record state, in memory, so Dig can pick synchronously inside
// the tap (iPhone needs the first play to come from a tap; a database read
// in between would break that). Filled from IndexedDB when Dig opens, and
// written back through the dig repository.

import type { Mark, RecordState } from '../../data/types';
import { loadRecordStates } from '../../db/repos/dig';
import type { Candidate } from '../../lib/dig';
import { emptySession, type Session } from '../../lib/session';

export const recordState: { heardAt: Record<string, number>; bad: Set<string>; notRecord: Map<string, NonNullable<RecordState['notRecord']>>; favourites: Set<string> } = {
  heardAt: {},
  bad: new Set(),
  notRecord: new Map(),
  favourites: new Set(),
};

export async function loadRecordState(): Promise<void> {
  const s = await loadRecordStates();
  recordState.heardAt = s.heardAt;
  recordState.bad = s.bad;
  recordState.notRecord = s.notRecord;
  recordState.favourites = s.favourites;
}

/**
 * One line of the dig session (SPEC F6): what Prev and Next walk through. `candidate` is the crate record
 * when there is one; a reopened mark or history row whose record has left the crate has none, and plays by video ID.
 */
export interface DigItem {
  videoId: string;
  candidate?: Candidate;
  /** Set when reopened from Marks */
  mark?: Mark;
  /** Reopened from Marks or History: an error 100/101/150 is reported, never auto-skipped (the user asked for this record) */
  reopened: boolean;
}

/**
 * The session, kept outside the screen so it survives a visit to Marks or History (and Prev and Next still
 * work after a reopen). In memory only: a reload starts a new one.
 */
export const digSession: { current: Session<DigItem> } = { current: emptySession() };

/**
 * The channels of the last digs, newest first (SPEC F6 repeat window). In memory, like the session: a reload starts afresh.
 * Only a dig adds to it; Prev, Next through the history and reopened records leave it alone.
 */
export const recentChannels: { ids: string[] } = { ids: [] };
export const noteDugChannel = (channelId: string): void => {
  recentChannels.ids = [channelId, ...recentChannels.ids].slice(0, 3);
};
